import React, { useCallback, useState } from "react";
import { styled } from "@mui/material/styles";
import { Checkbox } from "@mui/material";
import uniqBy from "lodash/uniqBy";
import isEqual from "lodash/isEqual";

import { CaseLabel as OptionType } from "interfaces/case-label";
import { Autocomplete } from "components/Autocomplete";
import { useLazyGetCaseLabels } from "hooks/case-labels/useLazyGetCaseLabels";
import { filterOptions } from "components/CasesTable/Filters/utils/filterOptions";

interface CaseLabelFilterProps {
  selectedCaseLabels: OptionType[];
  onChangeSelectedCaseLabels: (values: OptionType[]) => void;
  showChips?: boolean;
}

const selectAllOption: OptionType = {
  id: -1,
  name: "Select All",
  create_user: 0
};

export const CaseLabelFilter = ({
  selectedCaseLabels,
  onChangeSelectedCaseLabels,
  showChips = true
}: CaseLabelFilterProps): JSX.Element => {
  const {
    loading: caseLabelLoading,
    error: caseLabelError,
    fetchCaseLabels: getCaseLabels
  } = useLazyGetCaseLabels();
  const [options, setOptions] = useState<OptionType[] | undefined>(undefined);
  const [selectedState, setSelectedState] = useState<OptionType[]>([]);
  const [inputValue, setInputValue] = useState("");

  const onFocus = useCallback(() => {
    /** If it is the first focus event, get options. */
    if (options === undefined) {
      getCaseLabels().then(response => {
        if (response.results) {
          setOptions([selectAllOption, ...response.results]);
        }
      });
    }
    setSelectedState(selectedCaseLabels);
  }, [options, getCaseLabels, selectedCaseLabels]);

  return (
    <StyledAutocomplete
      label="Label"
      placeholder={showChips ? "Label" : ""}
      loading={caseLabelLoading}
      error={caseLabelError}
      showChips={showChips}
      size="small"
      multiple
      autoHighlight
      disableCloseOnSelect
      disableClearable
      autoComplete
      onFocus={onFocus}
      options={options ?? []}
      noOptionsText="No results found"
      getOptionLabel={(option: OptionType) => option.name}
      isOptionEqualToValue={(option: OptionType, value: OptionType) =>
        option.name === value.name
      }
      value={selectedCaseLabels}
      onInputChange={(
        _event: React.ChangeEvent,
        newInputValue: string
      ): void => {
        if (inputValue !== newInputValue) {
          setInputValue(newInputValue);
        }
      }}
      filterOptions={filterOptions}
      renderOption={(
        props: any,
        option: OptionType,
        { selected }: { selected: any }
      ) => {
        let { key, ...optionProps } = props;
        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");
        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        const isSelected =
          option.name.toLowerCase() === "select all"
            ? isEqual(filteredSelectedState, filteredNonSelectAllOptions)
            : selected;

        optionProps = {
          ...optionProps,
          "aria-selected": isSelected
        };

        return (
          <li key={key} {...optionProps}>
            <Checkbox checked={isSelected} />
            {option.name}
          </li>
        );
      }}
      onChange={(_event: React.ChangeEvent, values: OptionType[]) => {
        if (!options?.length) {
          return;
        }

        const filteredNonSelectAllOptions = filterOptions(options, {
          inputValue
        }).filter(option => option.name !== "Select All");

        const filteredSelectedState = filteredNonSelectAllOptions.filter(
          option => selectedState.some(selected => selected.id === option.id)
        );
        if (values.some(value => value.name.toLowerCase() === "select all")) {
          // If current selection includes "Select All"
          if (isEqual(filteredSelectedState, filteredNonSelectAllOptions)) {
            // If everything was selected, deselect all
            const selected = selectedState.filter(
              option =>
                !filteredSelectedState.some(
                  selected => selected.id === option.id
                )
            );
            onChangeSelectedCaseLabels(selected);
            setSelectedState(selected);
          } else {
            const selected = uniqBy(
              [...selectedCaseLabels, ...filteredNonSelectAllOptions],
              "id"
            );

            // If not everything was selected, select all
            onChangeSelectedCaseLabels(selected);
            setSelectedState(selected);
          }
        } else {
          // Normal selection of individual items
          onChangeSelectedCaseLabels(values);
          setSelectedState(values);
        }
      }}
    />
  );
};

const StyledAutocomplete = styled(Autocomplete)`
  height: 100%;
`;
